import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { OutcomesResponse } from '../../services/caseWorkflowService';
import { transitionLabel } from '../CaseWorkflow/caseWorkflowVocabulary';
import { OUTCOME_TRANSITIONS, OUTCOMES_CAPTION } from './projectOutcomesVocabulary';
import { dsVar } from '../../theme/tokens';

interface OutcomesStripProps {
    outcomes: OutcomesResponse;
    windowDays: number;
    onOpenCaseOperations: () => void;
}

/** What the collapsed details toggle says, so a reader knows whether to open it. */
export const decisionsHint = (gates: OutcomesResponse['gates']): string => {
    const decided = gates.reduce((sum, g) => sum + g.total, 0);
    const overridden = gates.reduce((sum, g) => sum + g.overridden, 0);
    if (!decided) return 'no decisions settled';
    if (overridden) return `${overridden.toLocaleString()} overridden by a reviewer`;
    return 'all automatic or confirmed';
};

/**
 * What happened to cases in the window, as headline numbers: the case-grain
 * transitions a case passes through, those that happened at all.
 *
 * Each gate's settled decisions — grouped by the outcomes the contract declares,
 * and split by who settled them — fold away behind "Decision details", closed by
 * default. Most of those counts restate a headline (auto-closed, referred, SAR),
 * and they count review decisions including re-runs rather than cases, so beside
 * the headlines they read as figures that fail to reconcile. What only they carry
 * — overrides, and cases not referred — is named on the toggle.
 *
 * Numbers in text tokens throughout; nothing here is told apart by colour.
 */
const OutcomesStrip: React.FC<OutcomesStripProps> = ({ outcomes, windowDays, onOpenCaseOperations }) => {
    const [showDetails, setShowDetails] = useState(false);
    const byCode = new Map(outcomes.transitions.map((t) => [t.code, t]));
    const headlines = (OUTCOME_TRANSITIONS.map((code) => byCode.get(code)).filter(Boolean) as OutcomesResponse['transitions'])
        .filter((t) => t.cases > 0);
    return (
        <section aria-label="Outcomes" className="text-left rounded-xl border border-line bg-surface/40 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
                <InsightsIcon sx={{ fontSize: 16, color: dsVar('fg-2') }} />
                <h3 className="text-sm font-semibold text-fg m-0">Outcomes · last {windowDays} days</h3>
                <button
                    type="button"
                    onClick={onOpenCaseOperations}
                    className="ml-auto bg-transparent border-none p-0 cursor-pointer flex items-center gap-1 text-xs font-mono text-accent-fg/70 hover:text-accent-fg"
                >
                    Case operations
                    <ArrowForwardIcon style={{ fontSize: 14 }} />
                </button>
            </div>

            {headlines.length ? (
                <dl className="flex flex-wrap gap-x-6 gap-y-2 m-0">
                    {headlines.map((t) => (
                        <div key={t.code} className="min-w-0">
                            <dt className="text-[11px] text-fg-2 whitespace-nowrap">{transitionLabel(t.code)}</dt>
                            <dd className="m-0 text-lg font-bold text-fg leading-tight">
                                {t.cases.toLocaleString()}
                                <span className="text-[11px] font-normal text-fg-muted"> {t.code === 'ALERT_QUEUED' ? 'cases w/ alerts' : 'cases'}</span>
                            </dd>
                        </div>
                    ))}
                </dl>
            ) : (
                <p className="text-xs text-fg-muted m-0">No case moved in the last {windowDays} days.</p>
            )}

            <button
                type="button"
                aria-expanded={showDetails}
                onClick={() => setShowDetails((open) => !open)}
                className="mt-2 bg-transparent border-none p-0 cursor-pointer flex items-center gap-1 text-xs text-fg-2 hover:text-fg-body"
            >
                <ExpandMoreIcon sx={{ fontSize: 16 }} className={`transition-transform ${showDetails ? '' : '-rotate-90'}`} />
                Decision details
                <span className="text-fg-muted">· {decisionsHint(outcomes.gates)}</span>
            </button>

            {showDetails && (
                <div className="mt-2 pt-2 border-t border-line">
                    <p className="text-[11px] text-fg-muted m-0 mb-2">{OUTCOMES_CAPTION(windowDays)}</p>
                    <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
                        {outcomes.gates.map((gate) => (
                            <div key={gate.code} className="min-w-0">
                                <div className="text-xs font-medium text-fg-soft">
                                    {gate.label}: {gate.total.toLocaleString()} {gate.total === 1 ? 'decision' : 'decisions'}
                                </div>
                                {gate.total > 0 ? (
                                    <>
                                        <ul className="list-none p-0 m-0 mt-1 space-y-0.5">
                                            {gate.outcomes.map((o) => (
                                                <li key={o.code} className="text-xs text-fg-2 flex justify-between gap-2">
                                                    <span>{o.label}</span>
                                                    <span className="text-fg-body font-semibold">{o.count.toLocaleString()}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <Tooltip
                                            title="Confirmed: a reviewer agreed with the suggestion. Overridden: a reviewer chose differently. Automatic: settled by the gate's configuration with no reviewer."
                                            arrow
                                            placement="top"
                                            describeChild
                                        >
                                            <div className="text-[11px] text-fg-muted mt-1 cursor-help">
                                                {gate.approved.toLocaleString()} confirmed · {gate.overridden.toLocaleString()} overridden · {gate.autoApproved.toLocaleString()} automatic
                                            </div>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <div className="text-[11px] text-fg-muted mt-1">No decisions settled in this window.</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

export default OutcomesStrip;
